import { ReactNode } from "react";
import { cx } from "@app/shared/utils/cx";
import styles from "./PageShell.module.css";

export type PageShellVariant =
  | "default"
  /**
   * Full-viewport background (`children`) with `topBar`/`bottomBar`
   * floating in a centered, mobile-width column on top of it — the
   * "native app over a full-screen map" pattern. Only the Home page uses
   * this today (`children` is `<MapView>`) — see PageShell.module.css for
   * how the layering/centering/pointer-events actually work.
   */
  | "map";

export type PageShellProps = {
  topBar?: ReactNode;
  bottomBar?: ReactNode;
  noPadding?: boolean;
  variant?: PageShellVariant;
  children: ReactNode;
};

/**
 * Every v2 page is `<PageShell>`. Default variant pins the single-column,
 * mobile-first frame (see `--layout-max-width` in tokens.css) as a normal
 * scrolling document with an optional sticky top/bottom bar. The `"map"`
 * variant is structurally different — see `PageShellVariant` — and exists
 * so Home doesn't have to reinvent full-viewport/safe-area/centering
 * mechanics that belong here.
 */
export const PageShell = ({ topBar, bottomBar, noPadding = false, variant = "default", children }: PageShellProps) => {
  if (variant === "map") {
    return (
      <div className={styles.mapShell}>
        <div className={styles.mapBackground}>{children}</div>
        <div className={styles.mapColumn}>
          {topBar ? <div className={styles.interactiveLayer}>{topBar}</div> : null}
          <div className={styles.mapSpacer} />
          {bottomBar ? <div className={styles.interactiveLayer}>{bottomBar}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      {topBar}
      <div className={cx(styles.content, noPadding && styles.noPadding)}>{children}</div>
      {bottomBar}
    </div>
  );
};
